import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type AdminRole = 'SUPER_ADMIN' | 'ADMIN' | 'VIEWER' | 'BRANCH_SALES';

/** Thai labels for admin roles, shared by nav, user list and user detail. */
export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'ผู้ดูแลระบบสูงสุด',
  ADMIN: 'ผู้ดูแลระบบ',
  VIEWER: 'ผู้ใช้ทั่วไป',
  BRANCH_SALES: 'พนักงานขายหน้าสาขา',
};

export function getAdminRoleLabel(role?: string | null): string {
  return (role && ADMIN_ROLE_LABELS[role as AdminRole]) || role || '';
}

/** Tailwind classes of the role badge in user list and user detail. */
export const ADMIN_ROLE_BADGE_CLASSES: Record<AdminRole, string> = {
  SUPER_ADMIN: 'bg-purple-100 text-purple-800',
  ADMIN: 'bg-blue-100 text-blue-800',
  VIEWER: 'bg-gray-100 text-gray-800',
  BRANCH_SALES: 'bg-emerald-100 text-emerald-800',
};

export function getAdminRoleBadgeClass(role?: string | null): string {
  return (
    (role && ADMIN_ROLE_BADGE_CLASSES[role as AdminRole]) ||
    'bg-gray-100 text-gray-800'
  );
}

/** Landing page of พนักงานขายหน้าสาขา (SE-2227). */
export const BRANCH_HOME_URL = '/admin/branch/customer-search';
export const DEFAULT_HOME_URL = '/admin/dashboard';

/**
 * URL prefixes a role may open; everything else redirects home. Roles not
 * listed (ADMIN, SUPER_ADMIN) may open every page. Mirrors @AdminRoles on the
 * API: VIEWER (ผู้ใช้ทั่วไป) only reads overview and statistics.
 */
const ROLE_ALLOWED_PREFIXES: Partial<Record<AdminRole, string[]>> = {
  BRANCH_SALES: ['/admin/branch', '/admin/documentation'],
  VIEWER: [
    '/admin/dashboard',
    '/admin/consent-dashboard',
    '/admin/reports',
    '/admin/documentation',
  ],
};

export interface AdminUser {
  id: string;
  username: string;
  fullName: string;
  email: string;
  role: AdminRole;
  isActive: boolean;
  /** สาขาของพนักงาน (SE-2227); null = ไม่ระบุสาขา. */
  branch?: string | null;
  lastLoginAt: string;
  createdAt: string;
  updatedAt: string;
  source?: 'LOCAL' | 'SSO';
  ssoSub?: string;
}

export interface AdminLoginResponse {
  accessToken: string;
  user: AdminUser;
}

export interface AdminLoginRequest {
  username: string;
  password: string;
}

export interface SSOLoginResponse {
  authorizationUrl: string;
  state: string;
}

export interface SSOCallbackResponse {
  accessToken: string;
  user: AdminUser;
  isNewUser: boolean;
}

export interface SSOStatusResponse {
  enabled: boolean;
  provider: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminAuthService {
  private apiUrl = environment.apiUrl || 'http://localhost:3000/api';
  private currentUserSubject = new BehaviorSubject<AdminUser | null>(null);
  private tokenKey = 'admin_token';
  private userKey = 'admin_user';

  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadStoredUser();
  }

  private loadStoredUser(): void {
    const token = localStorage.getItem(this.tokenKey);
    const userStr = localStorage.getItem(this.userKey);

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
      } catch (error) {
        this.logout();
      }
    }
  }

  login(credentials: AdminLoginRequest): Observable<AdminLoginResponse> {
    return this.http
      .post<AdminLoginResponse>(`${this.apiUrl}/admin/auth/login`, credentials)
      .pipe(
        tap((response) => {
          localStorage.setItem(this.tokenKey, response.accessToken);
          localStorage.setItem(this.userKey, JSON.stringify(response.user));
          this.currentUserSubject.next(response.user);
        })
      );
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    this.currentUserSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getCurrentUser(): AdminUser | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getCurrentUser();
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.role === role : false;
  }

  hasAnyRole(roles: string[]): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  }

  isBranchSales(user: AdminUser | null = this.getCurrentUser()): boolean {
    return user?.role === 'BRANCH_SALES';
  }

  /** Export is for ADMIN and SUPER_ADMIN only (the API returns 403 to others). */
  canExport(user: AdminUser | null = this.getCurrentUser()): boolean {
    return user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  }

  /** Where a user lands after login or when a route is not allowed. */
  getHomeUrl(user: AdminUser | null = this.getCurrentUser()): string {
    return this.isBranchSales(user) ? BRANCH_HOME_URL : DEFAULT_HOME_URL;
  }

  /**
   * Route allowlist per role (the backend enforces the same on the API),
   * see ROLE_ALLOWED_PREFIXES.
   */
  canAccessUrl(
    url: string,
    user: AdminUser | null = this.getCurrentUser()
  ): boolean {
    const allowed = user ? ROLE_ALLOWED_PREFIXES[user.role] : undefined;
    if (!allowed) {
      return true;
    }
    const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
    return allowed.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    );
  }

  /** Pick the URL to open after login: the requested one if allowed, else home. */
  resolvePostLoginUrl(requestedUrl?: string | null): string {
    const user = this.getCurrentUser();
    if (
      requestedUrl &&
      requestedUrl.startsWith('/admin') &&
      !requestedUrl.startsWith('/admin/login') &&
      this.canAccessUrl(requestedUrl, user)
    ) {
      return requestedUrl;
    }
    return this.getHomeUrl(user);
  }

  // SSO Methods

  getSSOStatus(): Observable<SSOStatusResponse> {
    return this.http.get<SSOStatusResponse>(
      `${this.apiUrl}/admin/auth/sso/status`
    );
  }

  initiateSSOLogin(returnUrl?: string): Observable<SSOLoginResponse> {
    const params: Record<string, string> = {};
    if (returnUrl) {
      params['returnUrl'] = returnUrl;
    }
    return this.http.get<SSOLoginResponse>(
      `${this.apiUrl}/admin/auth/sso/login`,
      { params }
    );
  }

  redirectToSSO(returnUrl?: string): void {
    this.initiateSSOLogin(returnUrl).subscribe({
      next: (response) => {
        // Store state for CSRF protection
        sessionStorage.setItem('sso_state', response.state);

        // Redirect to SSO
        window.location.href = response.authorizationUrl;
        console.log('Redirecting to SSO:', response.authorizationUrl);
      },
      error: (error) => {
        console.error('Failed to initiate SSO login:', error);
      },
    });
  }

  handleSSOCallback(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      console.error('SSO callback error:', error);
      this.handleSSOError(
        error,
        urlParams.get('error_description') || undefined
      );
      return;
    }

    if (code && state) {
      // Verify state for CSRF protection
      const storedState = sessionStorage.getItem('sso_state');

      if (state !== storedState) {
        console.error('Invalid state parameter - possible CSRF attack');
        this.handleSSOError('invalid_state', 'Invalid state parameter');
        return;
      }

      // Clean up stored state
      sessionStorage.removeItem('sso_state');

      // Process SSO callback
      this.processSSOCallback(code, state).subscribe({
        next: (response) => {
          // New SSO users outside sso.roleMapping are created inactive until
          // a SUPER_ADMIN grants a role (SE-2227); every API call would 401.
          if (!response.user?.isActive) {
            this.handleSSOError(
              'account_inactive',
              'บัญชีของคุณยังไม่เปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบเพื่อกำหนดสิทธิ์การใช้งาน'
            );
            return;
          }

          // Store tokens and user info
          localStorage.setItem(this.tokenKey, response.accessToken);
          localStorage.setItem(this.userKey, JSON.stringify(response.user));
          this.currentUserSubject.next(response.user);

          // Clean URL parameters
          this.cleanUrlParameters();

          // Optional: Show welcome message for new users
          if (response.isNewUser) {
            console.log('Welcome! Your account has been created.');
          }

          // Redirect to intended page or dashboard
          this.redirectAfterLogin();
        },
        error: (error) => {
          console.error('SSO callback processing failed:', error);
          this.handleSSOError(
            'callback_failed',
            'Failed to process SSO callback'
          );
        },
      });
    }
  }

  private processSSOCallback(
    code: string,
    state: string
  ): Observable<SSOCallbackResponse> {
    return this.http.get<SSOCallbackResponse>(
      `${this.apiUrl}/admin/auth/sso/callback`,
      {
        params: { code, state },
      }
    );
  }

  private handleSSOError(error: string, description?: string): void {
    // Log error
    console.error(`SSO Error: ${error}`, description);

    // Clean URL
    this.cleanUrlParameters();

    // Redirect to login page with error message
    const errorMessage = description || error;
    window.location.href = `/admin/login?error=${encodeURIComponent(
      errorMessage
    )}`;
  }

  private cleanUrlParameters(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');

    window.history.replaceState({}, document.title, url.pathname + url.hash);
  }

  private redirectAfterLogin(): void {
    // Check if there's a stored return URL
    const returnUrl = sessionStorage.getItem('returnUrl');
    sessionStorage.removeItem('returnUrl');

    window.location.href = this.resolvePostLoginUrl(returnUrl);
  }

  isSSOUser(): boolean {
    const user = this.getCurrentUser();
    return user?.source === 'SSO';
  }
}
